import React, { useEffect, useState } from "react";
import { X, AlertCircle, Check } from "lucide-react";
import api from "@/lib/api";

const DURATIONS = [
  { label: "7 days", days: 7 },
  { label: "14 days", days: 14 },
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
  { label: "6 months", days: 180 },
  { label: "1 year", days: 365 },
];
const REASONS = [
  "Support compensation",
  "Beta tester",
  "Partner",
  "Refund replacement",
  "Internal use",
  "Influencer",
  "Other",
];

/**
 * Comp (free upgrade) modal.
 * Props: userId, userEmail, onClose (with boolean "saved" flag)
 */
export default function CompModal({ userId, userEmail, onClose }) {
  const [plan, setPlan] = useState("growth");
  const [durationDays, setDurationDays] = useState(30);
  const [customDate, setCustomDate] = useState("");
  const [reason, setReason] = useState(REASONS[0]);
  const [notes, setNotes] = useState("");
  const [notify, setNotify] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (reason === "Other" && !notes.trim()) {
      setErr("Notes required when reason = Other");
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      await api.post(`/admin/users/${userId}/comp`, {
        comp_plan: plan,
        duration_days: customDate ? null : durationDays,
        expires_at: customDate
          ? new Date(customDate).toISOString()
          : null,
        reason,
        notes: notes || null,
        notify_user: notify,
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to grant comp");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4"
      data-testid="comp-modal"
      onClick={() => onClose(done)}
    >
      <div
        className="bg-zinc-950 border border-zinc-800 rounded-[4px] max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-4 border-b border-zinc-800">
          <div>
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
              Grant free upgrade
            </div>
            <h3 className="display mt-0.5 text-lg font-bold tracking-tight">
              {userEmail}
            </h3>
          </div>
          <button
            onClick={() => onClose(done)}
            data-testid="comp-close"
            className="text-zinc-500 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="p-6 text-center" data-testid="comp-success">
            <div className="mx-auto w-10 h-10 rounded-full bg-emerald-900/40 text-emerald-300 flex items-center justify-center">
              <Check size={18} />
            </div>
            <h4 className="display mt-3 text-lg font-bold">Comp granted</h4>
            <p className="mt-2 text-xs text-zinc-400">
              Plan updated and logged to admin audit.
            </p>
            <button
              onClick={() => onClose(true)}
              data-testid="comp-done"
              className="mt-4 bg-white text-black text-xs px-3 py-1.5 rounded-[3px] hover:bg-zinc-200"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-4 space-y-4">
            <div>
              <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Target plan
              </label>
              <div className="mt-1 flex gap-2">
                {["growth", "scale"].map((p) => (
                  <label
                    key={p}
                    data-testid={`comp-plan-${p}`}
                    className={`flex-1 border rounded-[3px] px-3 py-2 cursor-pointer text-center text-sm ${
                      plan === p
                        ? "border-white bg-zinc-900"
                        : "border-zinc-800 hover:bg-zinc-900"
                    }`}
                  >
                    <input
                      type="radio"
                      checked={plan === p}
                      onChange={() => setPlan(p)}
                      className="hidden"
                    />
                    <span className="display font-bold capitalize">{p}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Duration
              </label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {DURATIONS.map((d) => (
                  <button
                    key={d.days}
                    type="button"
                    onClick={() => {
                      setDurationDays(d.days);
                      setCustomDate("");
                    }}
                    data-testid={`comp-duration-${d.days}`}
                    className={`text-xs px-2 py-1 border rounded-[3px] ${
                      durationDays === d.days && !customDate
                        ? "bg-white text-black border-white"
                        : "border-zinc-800 hover:bg-zinc-900"
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                data-testid="comp-custom-date"
                className="mt-1.5 bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1 text-xs text-zinc-300"
              />
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Reason
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="comp-reason"
                className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs"
              >
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Notes {reason === "Other" && <span className="text-rose-400">*</span>}
              </label>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="comp-notes"
                className="mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-[3px] px-2 py-1.5 text-xs"
              />
            </div>

            <label className="flex items-center gap-2 text-xs text-zinc-300">
              <input
                type="checkbox"
                checked={notify}
                onChange={(e) => setNotify(e.target.checked)}
                data-testid="comp-notify"
              />
              Notify user by email
            </label>

            {err && (
              <div className="flex items-center gap-2 text-xs text-rose-300 bg-rose-900/20 border border-rose-800 rounded-[3px] p-2">
                <AlertCircle size={12} /> {err}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 border-t border-zinc-800 pt-3">
              <button
                type="button"
                onClick={() => onClose(done)}
                className="text-xs text-zinc-400 hover:text-white px-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                data-testid="comp-submit"
                className="bg-white text-black text-xs px-3 py-1.5 rounded-[3px] hover:bg-zinc-200 disabled:opacity-50"
              >
                {loading ? "Granting…" : "Grant comp"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
